import { destination, init } from "@/services/port.service";
import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/auth.store";
import type { OriginList, TicketTypeList } from "@/types/init";
import type { DestinationList } from "@/types/destination";
import { toast } from "sonner";

export function usePorts({
  initialOriginId = "",
  initialDestinationId = "",
}: {
  initialOriginId?: string;
  initialDestinationId?: string;
} = {}) {
  const [originList, setOriginList] = useState<OriginList[]>([]);
  const [ticketTypeList, setTicketTypeList] = useState<TicketTypeList[]>([]);
  const [destinationList, setDestinationList] = useState<DestinationList[]>([]);

  const [selectedOriginId, setSelectedOriginId] = useState(initialOriginId);
  const [selectedDestinationId, setSelectedDestinationId] =
    useState(initialDestinationId);

  const [initLoading, setInitLoading] = useState(true);

  // Fetch init ONCE
  useEffect(() => {
    async function fetchInit() {
      try {
        const data = await init({
          agentCompanyId: useAuthStore.getState().user?.agentCompanyId || "",
          agentCompanySellerId:
            useAuthStore.getState().user?.agentCompanySellerId || "",
        });

        const origins = data.data.originList;
        const passengers = data.data.ticketTypeList;

        setOriginList(origins);
        setTicketTypeList(passengers);

        const defaultOrigin =
          origins.find((jetty) => jetty.mdTerminalId === initialOriginId) ??
          origins.find((jetty) =>
            jetty.mdTerminalName.toLowerCase().includes("mersing"),
          );

        if (defaultOrigin) {
          setSelectedOriginId(defaultOrigin.mdTerminalId);
        } else {
          setInitLoading(false);
        }
      } catch (error) {
        console.error("Failed to fetch init data", error);
        toast.error("Failed to load initial data.");
        setInitLoading(false);
      }
    }

    fetchInit();
  }, []);

  // Fetch destination whenever origin changes
  useEffect(() => {
    if (!selectedOriginId) return;

    async function fetchDestination() {
      try {
        const data = await destination({
          agentCompanyId: useAuthStore.getState().user?.agentCompanyId || "",
          agentCompanySellerId:
            useAuthStore.getState().user?.agentCompanySellerId || "",
          mdTerminalIdFrom: selectedOriginId,
        });

        const destinations = data.data.destinationList;

        setDestinationList(destinations);

        const defaultDestination =
          destinations.find(
            (jetty) => jetty.mdTerminalId === initialDestinationId,
          ) ??
          destinations.find((jetty) =>
            jetty.mdTerminalName.toLowerCase().includes("genting"),
          );

        setSelectedDestinationId(defaultDestination?.mdTerminalId ?? "");
      } catch (error) {
        console.error("Failed to fetch destination data", error);
        toast.error("Failed to fetch destination data.");

        setDestinationList([]);
        setSelectedDestinationId("");
      } finally {
        setInitLoading(false);
      }
    }

    fetchDestination();
  }, [selectedOriginId]);

  return {
    originList,
    destinationList,
    ticketTypeList,

    originId: selectedOriginId,
    setOriginId: setSelectedOriginId,

    destinationId: selectedDestinationId,
    setDestinationId: setSelectedDestinationId,

    initLoading,
  };
}
